import { observer, Observer } from "mobx-react";
import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from "@material-ui/core";
import { autorun, computed, observable, toJS } from "mobx";
import { Icon } from "react-native-elements";
import { isEmpty, isEqual, safeParseJSON } from "../../../../../utils/helpers";
import { theme } from "../../../../../config/style-configs/theme";
import { placesService } from "../../../../../cdm/services/places-service";
import { formService } from "../../../../../cdm/services/form-service";
import { typeClassIds } from "../../../../../config/variable-config";
import { View } from "react-native";
import { matchOSMCityWithTimeZone } from "../../../../../custom/mcb/lib/group-utilities-mcb";

@observer
export class MapAndLocation extends React.Component {
  disposer;

  mapDefaultZoom = 13;
  @observable cityCoordinates;
  @observable mapZoom = this.mapDefaultZoom;
  @observable mapCenter;
  @observable mapTypeId;

  // placeSelector?: GooglePlaceSelector;
  autocomplete = new google.maps.places.AutocompleteService();
  places = new google.maps.places.PlacesService(document.createElement("div"));

  @observable lat;
  @observable lng;
  @observable placeId = "";

  @observable careCircleTypeClass;

  @observable addressData = {
    house_number: "",
    road: "",
    room: "",
    city: "",
    province: "",
    state: "",
    postcode: ""
  };

  @observable timezone = "";
  @observable pendingProvince = "";
  @observable pendingTimezone = "";

  @observable suggestions = observable([]);

  @observable enteredLocation = {};

  @computed get requiredFields() {
    return ["house_number", "road", "city", "postcode"];
  }

  @computed get provinceOptions() {
    const provinceField = this.careCircleTypeClass.find(
      field => field.name === "provinces"
    );
    const options = provinceField.options || [];
    if (!this.addressData.province && options[1]) {
      setTimeout(() => {
        this.addressData.province = options[1].name;
        const timezone = this.timezoneOptions.find(
          t => t.dependOnValue && t.dependOnValue === options[1].name
        );
        if (timezone) {
          this.timezone = timezone.name;
        }
      });
    }
    return options;
  }
  @computed get visibleProvinceOptions() {
    return this.provinceOptions.filter(option =>
      this.timezoneOptions.some(
        t => t.dependOnValue && t.dependOnValue.includes(option.name)
      )
    );
  }
  @computed get timezoneOptions() {
    const timezoneField = this.careCircleTypeClass.find(
      field => field.name === "timezone"
    );
    return timezoneField.options || [];
  }

  @computed get mapViewportCenter() {
    const location = isEmpty(this.enteredLocation)
      ? this.cityCoordinates
      : this.enteredLocation;
    if (!location) return;
    return new google.maps.LatLng({
      lat: Number(location.lat),
      lng: Number(location.lng || location.lon)
    });
  }

  @computed get canSave() {
    return (
      !isEmpty(this.enteredLocation) &&
      !isEqual((this.props.location || {}).location, this.enteredLocation)
    );
  }

  constructor(props) {
    super(props);
    this.mapTypeId = google.maps.MapTypeId.ROADMAP;
    formService
      .findFormClassByIdAsync(typeClassIds.careReceiverProfile)
      .then(form => {
        this.careCircleTypeClass = safeParseJSON(form.metadata);
      });
  }

  async componentDidMount() {
    const { location } = this.props.location || {};
    if (location) this.enteredLocation = JSON.parse(JSON.stringify(location));
    if (this.enteredLocation) {
      Object.assign(this.addressData, this.enteredLocation.address || {});
      this.lat = Number(this.enteredLocation.lat);
      this.lng = Number(this.enteredLocation.lon || this.enteredLocation.lng);
      this.placeId = this.enteredLocation.place_id;
    }
    this.disposer = autorun(this.storeGeocodingResult);
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (!isEqual(this.props.location, prevProps.location)) {
      const { location } = this.props.location || {};
      if (location) this.enteredLocation = JSON.parse(JSON.stringify(location));
    }
  }

  syncTimezone = value => {
    const timezone = this.timezoneOptions.find(
      t => t.dependOnValue && t.dependOnValue === value
    );
    if (timezone) {
      this.timezone = timezone.name;
      this.pendingTimezone = timezone.name;
    }
  };

  handleAddressChange = event => {
    const { name, value } = event.target || {};
    if (!name) return;
    this.addressData[name] = value;
    if (name === "province") {
      this.pendingProvince = value;
      this.syncTimezone(value);
    }
    if (["city", "road", "house_number", "postcode"].includes(name)) {
      this.placeId = "";
      this.enteredLocation = {};
    }
  };

  handleAddressBlur = event => {
    const { name, value } = event.target || {};
    if (!name) return;
    this.addressData[name] = value;
    this.suggestAddress();
  };

  suggestAddress = async () => {
    const { house_number, road, city } = this.addressData;
    if (!road && !house_number) return;
    const input = `${house_number || ""} ${road || ""} ${city || ""}`.trim();
    return this.autocomplete.getPlacePredictions(
      {
        input,
        types: ["address"],
        componentRestrictions: { country: "ca" }
      },
      this.displayPredictions
    );
  };

  displayPredictions = (predictions, status) => {
    if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions)
      return;
    if (predictions.some(p => p.place_id === this.placeId)) return;
    this.suggestions.replace(predictions);
  };

  replaceAddress = async suggestion => {
    const { place_id } = suggestion;
    if (!place_id) return;
    this.placeId = place_id;
    const addressDetails = await new Promise(resolve =>
      this.places.getDetails({ placeId: place_id }, resolve)
    );
    if (
      isEmpty(addressDetails) ||
      !addressDetails.address_components ||
      isEmpty(addressDetails.geometry)
    )
      return;
    const { lat, lng } = addressDetails.geometry.location.toJSON() || {};
    this.lat = lat;
    this.lng = lng;
    if (!lat || !lng) return;
    const getComponent = (type, useShort) => {
      const part = addressDetails.address_components.find(c =>
        c.types.includes(type)
      );
      if (!part) return "";
      return useShort ? part.short_name : part.long_name;
    };
    const autoAddressData = {
      house_number: getComponent("street_number"),
      room: getComponent(""),
      road: getComponent("route"),
      city:
        getComponent("locality") ||
        getComponent("sublocality") ||
        getComponent("administrative_area_level_2"),
      postcode: getComponent("postal_code"),
      province: getComponent("administrative_area_level_1", true).toUpperCase()
    };
    if (!isEmpty(autoAddressData)) {
      Object.assign(this.addressData, {
        ...autoAddressData,
        state: autoAddressData.province
          ? getComponent("administrative_area_level_1")
          : undefined
      });
      if (autoAddressData.province) {
        this.pendingProvince = autoAddressData.province;
        this.syncTimezone(autoAddressData.province);
      }
    }
    this.suggestions.clear();
    return this.storeGeocodingResult();
  };

  storeGeocodingResult = async () => {
    const lat = this.lat;
    const lng = this.lng;
    if (!lat || !lng) return;
    const formatAddress = address =>
      `${
        address.room ? `${address.room}${address.house_number ? "-" : ""}` : ""
      }${address.house_number ? `${address.house_number} ` : ""}${
        address.road ? `${address.road}, ` : ""
      }${address.city ? `${address.city}, ` : ""}${
        address.state ? `${address.state}, ` : ""
      }${address.postcode ? `${address.postcode}` : ""}`.trim();
    const osmGeocodingResults = await placesService.geocodeLatLngIntoOSMResult({
      lat,
      lng
    });

    const timezoneField = this.careCircleTypeClass.find(
      field => field.name === "timezone"
    );
    const matchedTimezone = matchOSMCityWithTimeZone(
      timezoneField,
      osmGeocodingResults
    );
    if (matchedTimezone) {
      this.timezone = matchedTimezone;
      this.pendingTimezone = matchedTimezone;
    }

    this.enteredLocation = {
      ...osmGeocodingResults,
      address: toJS(this.addressData),
      place_id: this.placeId,
      formattedAddress: formatAddress(this.addressData)
    };
  };

  handleClose = event => {
    // if (this.isEditingOrAdding) return this.scrollAndFlash();
    this.pendingProvince = "";
    this.pendingTimezone = "";
    this.props.onClose && this.props.onClose(event);
  };

  handleSave = () => {
    if (this.pendingProvince) {
      this.props.onProvinceUpdate &&
        this.props.onProvinceUpdate(this.pendingProvince);
    }
    if (this.pendingTimezone) {
      this.props.onTimezoneUpdate &&
        this.props.onTimezoneUpdate(this.pendingTimezone);
    }
    this.props.onLocationUpdate &&
      this.props.onLocationUpdate(this.enteredLocation);
    this.handleClose();
  };

  renderProvinceItems = () =>
    this.visibleProvinceOptions
      .map(option => (
        <MenuItem key={option.name} value={option.name}>
          {option.placeholder}
        </MenuItem>
      ))
      .filter(Boolean);

  renderAddressFields = () => (
    <Observer>
      {() => (
        <View>
          <View style={{ flexDirection: "column", marginVertical: 16 }}>
            <TextField
              variant="outlined"
              required={this.requiredFields.includes("house_number")}
              label="Street Number"
              name="house_number"
              value={this.addressData.house_number || ""}
              onChange={this.handleAddressChange}
              onBlur={this.handleAddressBlur}
            />
          </View>
          <View style={{ flexDirection: "column", marginVertical: 16 }}>
            <TextField
              variant="outlined"
              required={this.requiredFields.includes("road")}
              label="Street Name"
              name="road"
              value={this.addressData.road || ""}
              onChange={this.handleAddressChange}
              onBlur={this.handleAddressBlur}
            />
          </View>
          {!isEmpty(this.suggestions) && (
            <View style={{ flexDirection: "column", paddingVertical: 16 }}>
              <Typography className="font-xs" style={{ color: theme.color }}>
                Suggested Addresses
              </Typography>
              {this.suggestions.map(suggestion => (
                <View key={suggestion.place_id} style={{ marginVertical: 8 }}>
                  <Typography
                    style={{
                      textDecoration: "underline",
                      cursor: "pointer",
                      color: theme.color
                    }}
                    onClick={() => this.replaceAddress(suggestion)}
                  >
                    {suggestion.description}
                  </Typography>
                </View>
              ))}
            </View>
          )}
          <View style={{ flexDirection: "column", marginVertical: 16 }}>
            <TextField
              variant="outlined"
              required={this.requiredFields.includes("room")}
              label="Apt/Suite/Unit Number"
              name="room"
              value={this.addressData.room || ""}
              onChange={this.handleAddressChange}
              onBlur={this.handleAddressBlur}
            />
          </View>
          <View style={{ flexDirection: "column", marginVertical: 16 }}>
            <TextField
              variant="outlined"
              required={this.requiredFields.includes("city")}
              label="City"
              name="city"
              value={this.addressData.city || ""}
              onChange={this.handleAddressChange}
              onBlur={this.handleAddressBlur}
            />
          </View>
          <View style={{ flexDirection: "column", marginVertical: 16 }}>
            <FormControl variant="outlined">
              <InputLabel id="province-label">Province</InputLabel>
              <Select
                required={this.requiredFields.includes("province")}
                defaultValue="BC"
                labelId="province-label"
                label="Province"
                name="province"
                value={this.addressData.province}
                onChange={this.handleAddressChange}
                onBlur={this.handleAddressBlur}
              >
                {this.renderProvinceItems()}
              </Select>
            </FormControl>
          </View>
          <View style={{ flexDirection: "column", marginVertical: 16 }}>
            <TextField
              variant="outlined"
              required={this.requiredFields.includes("postcode")}
              label="Postal Code"
              name="postcode"
              value={this.addressData.postcode || ""}
              onChange={this.handleAddressChange}
              onBlur={this.handleAddressBlur}
            />
          </View>
        </View>
      )}
    </Observer>
  );

  render() {
    if (!placesService.initialized) return null;

    return (
      <Dialog onClose={this.handleClose} open={this.props.isOpen}>
        <DialogTitle style={{ padding: "5px 10px 5px 24px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            <span>Edit location</span>
            <IconButton onClick={this.handleClose}>
              <Icon name="close" type="material-community" size={22} />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent
          ref={this.modalContentRef}
          dividers
          style={{ minWidth: 250, display: "flex", flexDirection: "column" }}
        >
          {this.renderAddressFields()}
        </DialogContent>
        <DialogActions
          style={{
            display: "flex",
            justifyContent: "center"
          }}
        >
          <Button
            color="primary"
            style={{ color: this.canSave ? theme.color : undefined }}
            disabled={!this.canSave}
            onClick={this.handleSave}
          >
            Save location
          </Button>
          {/*<Button onClick={this.handleClose} color="primary">*/}
          {/*  Done*/}
          {/*</Button>*/}
        </DialogActions>
      </Dialog>
    );
  }
}
